import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// "Add to the DPK" on the New/Edit Release page (owner directive 2026-09-09,
// four-seat committee). The drop itself uploads: the browser puts the bytes
// on the bucket (Active Storage direct upload, progress from the PUT), then
// the fragment door attaches the blob and answers with the section body
// re-rendered from the database, which replaces what is on the page. The
// only state kept here is the upload queue: ONE upload at a time, in drop
// order, so tracks land in the order they were dropped and album creation
// can never race between the music and artwork zones.
//
// Stop upload aborts the in-flight request; an aborted PUT never becomes an
// object and the never-attached blob is swept. Create/Update Release is held
// ("Uploading 1 of 3...") while anything is in flight, so a submit can never
// cut an upload off. Dropping a file outside a zone is swallowed at the
// document so the tab never navigates to the MP3.
export default class extends Controller {
  static targets = ["albumId", "pending", "body", "gated"]
  static values = { urlTemplate: String, directUploadUrl: String, releaseId: String, clientField: String }

  connect() {
    this.queue = []
    this.inflight = null
    this.batchTotal = 0
    this.batchDone = 0
    this.form = this.element.closest("form")
    this.fields = {
      client: this.form?.querySelector("[name$='[client_id]']"),
      name: this.form?.querySelector("[name$='[name]']"),
      type: this.form?.querySelector("[name$='[release_type]']")
    }
    this.submitButton = this.form?.querySelector("input[type='submit'], button[type='submit']")
    this.submitLabel = this.submitButton?.value || this.submitButton?.textContent

    this.onFieldChange = (e) => this.fieldChanged(e)
    Object.values(this.fields).forEach(f => f?.addEventListener("change", this.onFieldChange))
    this.onSubmit = (e) => {
      if (!this.busy) return
      e.preventDefault()
      this.note("Wait for the upload to finish, then save the release.")
    }
    this.form?.addEventListener("submit", this.onSubmit)
    this.onBeforeUnload = (e) => { if (this.busy) { e.preventDefault(); e.returnValue = "" } }
    window.addEventListener("beforeunload", this.onBeforeUnload)
    // A file dropped an inch outside a zone must not navigate the tab away.
    this.onDocumentDrag = (e) => { if (!e.target.closest?.("[data-dpk-zone]")) e.preventDefault() }
    document.addEventListener("dragover", this.onDocumentDrag)
    document.addEventListener("drop", this.onDocumentDrag)

    this.applyLock()
  }

  disconnect() {
    Object.values(this.fields).forEach(f => f?.removeEventListener("change", this.onFieldChange))
    this.form?.removeEventListener("submit", this.onSubmit)
    window.removeEventListener("beforeunload", this.onBeforeUnload)
    document.removeEventListener("dragover", this.onDocumentDrag)
    document.removeEventListener("drop", this.onDocumentDrag)
  }

  // --- state ---

  get busy() { return !!this.inflight || this.queue.length > 0 }
  get state() { return this.bodyTarget.firstElementChild?.dataset || {} }
  get clientId() { return this.fields.client ? this.fields.client.value : this.state.clientId }

  url(extra = {}) {
    const base = this.urlTemplateValue.replace("CLIENT_ID", this.clientId)
    const params = new URLSearchParams({
      name: this.fields.name?.value?.trim() || "",
      release_type: this.fields.type?.value || "",
      dpk_album_id: this.albumIdTarget.value || "",
      release_id: this.releaseIdValue || "",
      ...extra
    })
    return { base, query: params.toString() }
  }

  // --- the form's own fields drive the section ---

  fieldChanged(event) {
    // In the video state the name only prefills the title; a re-render
    // would throw away a pasted embed code.
    if (event.target === this.fields.name && this.state.state === "video") return
    this.refresh()
  }

  async refresh() {
    if (this.busy) { this.refreshWhenIdle = true; return }
    if (!this.clientId) { this.setBody(this.gatedTarget.innerHTML); return }
    const { base, query } = this.url()
    const response = await this.request(`${base}?${query}`, { method: "GET" })
    if (response.ok) this.setBody(await response.text())
  }

  setBody(html) {
    if (html !== null) {
      this.bodyTarget.innerHTML = html
    }
    this.albumIdTarget.value = this.state.albumId || ""
    this.applyLock()
  }

  // Once something is in a client's DPK, the admin form's client select
  // locks. A disabled select does not submit, so a hidden mirror carries
  // the value; Remove everything to unlock.
  applyLock() {
    const select = this.fields.client
    if (!select || select.tagName !== "SELECT") return
    const locked = !!this.albumIdTarget.value
    select.disabled = locked
    let mirror = this.form.querySelector("[data-dpk-client-mirror]")
    let note = this.form.querySelector("[data-dpk-client-note]")
    if (locked) {
      if (!mirror) {
        mirror = document.createElement("input")
        mirror.type = "hidden"
        mirror.name = this.clientFieldValue
        mirror.dataset.dpkClientMirror = ""
        select.insertAdjacentElement("afterend", mirror)
      }
      mirror.value = select.value
      if (!note) {
        note = document.createElement("p")
        note.className = "text-xs text-[#8C8C8C] mt-1"
        note.dataset.dpkClientNote = ""
        note.textContent = "Client locked: files were added to this DPK. Remove them to change the client."
        mirror.insertAdjacentElement("afterend", note)
      }
    } else {
      mirror?.remove()
      note?.remove()
    }
  }

  // --- drop zones ---

  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.classList.add("border-[#C4A484]", "bg-[#FAF8F5]")
  }

  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.classList.remove("border-[#C4A484]", "bg-[#FAF8F5]")
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    const zone = event.currentTarget
    zone.classList.remove("border-[#C4A484]", "bg-[#FAF8F5]")
    this.enqueue(Array.from(event.dataTransfer.files), zone)
  }

  pick(event) {
    event.stopPropagation()
    const zone = event.currentTarget.closest("[data-dpk-zone]")
    zone?.querySelector("input[type='file']")?.click()
  }

  picked(event) {
    const zone = event.target.closest("[data-dpk-zone]")
    this.enqueue(Array.from(event.target.files), zone)
    event.target.value = ""
  }

  swallowEnter(event) {
    if (event.key === "Enter") { event.preventDefault(); event.target.blur() }
  }

  // Inline track title (owner directive 2026-09-09, second pass): saved on
  // change, the body re-rendered like every other edit here.
  async renameTrack(event) {
    const input = event.target
    const { base, query } = this.url()
    const body = new FormData()
    body.append("title", input.value)
    const response = await this.request(`${base}/${input.dataset.trackId}?${query}`, { method: "PATCH", body })
    if (response.ok) this.setBody(await response.text())
    else this.note("Could not save that title. Reload the page and try again.")
  }

  // --- the queue ---

  async enqueue(files, zone) {
    const kind = zone.dataset.kind
    const multiple = zone.dataset.multiple === "true"
    if (!multiple) files = files.slice(0, 1)
    if (multiple) files.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))

    for (const raw of files) {
      const file = this.withType(raw)
      const problem = await this.problemWith(file, kind)
      if (problem) { this.note(`${file.name}: ${problem}`); continue }

      const item = { file, kind, cancelled: false, xhr: null }
      if (kind === "track" && !multiple) {
        // A single IS the track: a new file replaces the one in flight or the one saved.
        this.queue = this.queue.filter(q => q.kind !== "track")
        if (this.inflight?.kind === "track") this.stopItem(this.inflight)
        item.replaceTrackId = this.state.singleTrackId || ""
      }
      if (kind === "cover") {
        this.queue = this.queue.filter(q => q.kind !== "cover")
        if (this.inflight?.kind === "cover") this.stopItem(this.inflight)
      }
      this.renderPending(item)
      this.queue.push(item)
      this.batchTotal++
    }
    this.pump()
  }

  pump() {
    if (this.inflight || this.queue.length === 0) {
      if (!this.inflight && this.queue.length === 0) this.batchFinished()
      return
    }
    this.inflight = this.queue.shift()
    this.updateSubmit()
    this.upload(this.inflight).finally(() => {
      this.inflight = null
      this.batchDone++
      this.pump()
    })
  }

  batchFinished() {
    this.batchTotal = 0
    this.batchDone = 0
    this.updateSubmit()
    if (this.refreshWhenIdle) { this.refreshWhenIdle = false; this.refresh() }
  }

  async upload(item) {
    this.setRow(item, { text: "Preparing...", progress: 0 })
    const blob = await this.directUpload(item)
    if (item.cancelled) { this.setRow(item, { text: "Upload stopped. Nothing was added.", done: true }); return }
    if (!blob) { this.setRow(item, { text: "Upload failed. Drop the file again to retry.", error: true, done: true }); return }

    this.setRow(item, { text: "Saving to the DPK...", progress: 100, saving: true })
    const body = new FormData()
    body.append("kind", item.kind)
    body.append("signed_id", blob.signed_id)
    body.append("filename", item.file.name)
    if (item.replaceTrackId) body.append("replace_track_id", item.replaceTrackId)
    const { base, query } = this.url()
    const response = await this.request(`${base}?${query}`, { method: "POST", body })
    if (response.ok) {
      this.setBody(await response.text())
      item.row?.remove()
    } else {
      const reason = response.status ? await response.text().catch(() => "") : ""
      this.setRow(item, { text: reason || "Could not save to the DPK. Reload the page and try again.", error: true, done: true })
    }
  }

  directUpload(item) {
    return new Promise(resolve => {
      const delegate = {
        directUploadWillCreateBlobWithXHR: (xhr) => { item.xhr = xhr },
        directUploadWillStoreFileWithXHR: (xhr) => {
          item.xhr = xhr
          xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) this.setRow(item, { progress: (e.loaded / e.total) * 100 })
          })
        }
      }
      new DirectUpload(item.file, this.directUploadUrlValue, delegate).create((error, blob) => {
        resolve(error ? null : blob)
      })
    })
  }

  stop(event) {
    const row = event.currentTarget.closest("[data-dpk-item]")
    const item = this.queue.find(q => q.row === row) || (this.inflight?.row === row ? this.inflight : null)
    if (!item) return
    if (this.queue.includes(item)) {
      this.queue = this.queue.filter(q => q !== item)
      this.batchTotal--
      this.setRow(item, { text: "Upload stopped. Nothing was added.", done: true })
      this.pump()
    } else {
      this.stopItem(item)
    }
  }

  stopItem(item) {
    item.cancelled = true
    item.xhr?.abort()
  }

  async remove(event) {
    const { kind, id, label } = event.currentTarget.dataset
    const ok = await window.appConfirm(
      `Remove ${label} from the DPK? Journalists may already be looking at it.`,
      { title: "Remove from the DPK?", confirmLabel: "Remove", cancelLabel: "Keep it" }
    )
    if (!ok) return
    const { base, query } = this.url({ kind })
    const response = await this.request(`${base}/${id}?${query}`, { method: "DELETE" })
    if (response.ok) this.setBody(await response.text())
    else this.note("Could not remove it. Reload the page and try again.")
  }

  // --- helpers ---

  request(url, options) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    return fetch(url, {
      ...options,
      headers: { "X-CSRF-Token": csrf, "Accept": "text/html" },
      credentials: "same-origin",
      redirect: "manual"
    }).catch(() => ({ ok: false, status: 0 }))
  }

  // Some browsers report no MIME type for a WAV (and a few for MP3); the
  // conversion decision reads the type, so resolve it from the extension.
  withType(file) {
    if (file.type) return file
    const ext = file.name.split(".").pop().toLowerCase()
    const type = { mp3: "audio/mpeg", wav: "audio/wav", jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png",
                   webp: "image/webp", gif: "image/gif", heic: "image/heic", heif: "image/heif" }[ext]
    return type ? new File([file], file.name, { type }) : file
  }

  async problemWith(file, kind) {
    const ext = file.name.split(".").pop().toLowerCase()
    if (kind === "track") {
      return ["mp3", "wav"].includes(ext) ? null : "Only MP3 and WAV files can be added as tracks."
    }
    if (!["jpg", "jpeg", "png", "webp", "gif", "heic", "heif"].includes(ext)) {
      return "Artwork has to be a JPG, PNG, WebP, HEIC, or GIF."
    }
    if (file.size > 10 * 1024 * 1024) return "Artwork has to be under 10MB."
    try {
      const bitmap = await createImageBitmap(file)
      const edge = Math.max(bitmap.width, bitmap.height)
      bitmap.close?.()
      if (edge > 10000) return `That image is ${edge}px on its longest side; the limit is 10000px.`
    } catch { /* HEIC and friends cannot be decoded here; the server checks */ }
    return null
  }

  renderPending(item) {
    const row = document.createElement("div")
    row.dataset.dpkItem = ""
    row.className = "flex items-center gap-3 text-sm"
    const mb = (item.file.size / 1024 / 1024).toFixed(1)
    row.innerHTML = `
      <span class="truncate max-w-[40%] text-[#2D2D2D]">${this.escape(item.file.name)} <span class="text-xs text-[#8C8C8C]">(${mb} MB)</span></span>
      <div class="flex-1 h-2 bg-[#F5F3F0] rounded-full overflow-hidden" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
        <div data-bar class="h-2 rounded-full bg-[#C4A484] transition-all duration-300" style="width: 0%"></div>
      </div>
      <span data-text class="text-xs text-[#8C8C8C] w-40 text-right">Waiting...</span>
      <button type="button" data-stop data-action="dpk-section#stop" class="text-xs text-[#8C8C8C] underline cursor-pointer">Stop upload</button>
    `
    this.pendingTarget.appendChild(row)
    item.row = row
  }

  setRow(item, { text, progress, error, done, saving } = {}) {
    const row = item.row
    if (!row) return
    if (progress !== undefined) {
      row.querySelector("[data-bar]").style.width = `${Math.round(progress)}%`
      row.querySelector("[role='progressbar']").setAttribute("aria-valuenow", Math.round(progress))
    }
    if (text !== undefined) {
      const el = row.querySelector("[data-text]")
      el.textContent = text
      el.className = `text-xs w-40 text-right ${error ? "text-[#E57373]" : "text-[#8C8C8C]"}`
    }
    if (saving || done) row.querySelector("[data-stop]")?.remove()
    if (done && !error) setTimeout(() => row.remove(), 4000)
  }

  updateSubmit() {
    if (!this.submitButton) return
    const busy = this.busy
    this.submitButton.disabled = busy
    this.submitButton.classList.toggle("opacity-50", busy)
    this.submitButton.classList.toggle("cursor-not-allowed", busy)
    const label = busy ? `Uploading ${Math.min(this.batchDone + 1, this.batchTotal)} of ${this.batchTotal}...` : this.submitLabel
    if ("value" in this.submitButton && this.submitButton.tagName === "INPUT") this.submitButton.value = label
    else this.submitButton.textContent = label
  }

  note(text) {
    const p = document.createElement("p")
    p.className = "text-xs text-[#E57373]"
    p.textContent = text
    this.pendingTarget.appendChild(p)
    setTimeout(() => p.remove(), 6000)
  }

  escape(text) {
    return text.replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }
}
